import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ResourceMultiSelectFilterClearBehaviorsEnum} from '../../components/common/filters/resourceMultiSelectFilterDropdown';
import {ResourceMultiSelectFilterQueryDropdown} from '../../components/common/filters/resourceMultiSelectFilterQueryDropdown';
import {GridAreaItem} from '../../components/common/grid';
import {TeammateModel} from '../../models/api/teammateModel';
import {useTeammatesSource} from '../fetchers/teammates';
import {CircleEditorTeammateDropdownRow} from './circleEditorTeammateDropdownRow';

/*
 * Consts.
 */

const DROPDOWN_WIDTH = 260;

/*
 * Props.
 */

interface CircleEditorTeammateDropdownProps {
  teammateIds: ReadonlyArray<number>;
  onChange: HandlerOf<ReadonlyArray<number>>;
  className?: string;
}

/*
 * Style.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  width: fit-content;
`;

const StyledButtonContentDiv = styled.div`
  display: grid;
  grid-template-areas: 'icon . text';
  grid-template-columns: max-content 9px max-content;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  grid-area: 'icon';
`;

/*
 * Component.
 */

export const CircleEditorTeammateDropdown: FC<CircleEditorTeammateDropdownProps> = (props) => {
  const {teammateIds, className, onChange} = props;

  const [query, setQuery] = useState('');
  const teammateSource = useTeammatesSource(query);

  const renderDropdown = useCallback(
    (popoverTransferProps: PopoverTransferProps) => (
      <ResourceMultiSelectFilterQueryDropdown<TeammateModel>
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...popoverTransferProps}
        dropdownSource={teammateSource}
        computeFilterGenerator={(item, selectedItemsIds, makeOnItemSelect) => {
          const isInitiallySelected = teammateIds.includes(item.id);
          const isSelected = selectedItemsIds.includes(item.id);

          return {
            isSelected,
            onSelect: makeOnItemSelect(item.id),
            renderer: () => <CircleEditorTeammateDropdownRow teammate={item} isChecked={isSelected} />,

            // Remove a teammate from a circle is done in the main table, not in this dropdown.
            isDisabled: isInitiallySelected,
            renderTooltip: isInitiallySelected ? renderDisabledTooltip : undefined,
          };
        }}
        dropdownTitle={<Trans id="circle-editor-teammate-dropdown-title">Add to group</Trans>}
        onSearchQuery={setQuery}
        searchQuery={query}
        onSetItems={onChange}
        selectedItemIds={teammateIds}
        applyButtonLabel={<Trans id="circle-editor-teammate-dropdown-apply">Add to group</Trans>}
        width={DROPDOWN_WIDTH}
        clearBehavior={ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_NEW_SELECTION}
      />
    ),
    [onChange, query, teammateIds, teammateSource],
  );

  return (
    <StyledDropdownBoxCoordinator
      className={className}
      shouldDisablePadding
      renderDropdown={renderDropdown}
      renderButton={renderButton}
    />
  );
};

/*
 * Helpers.
 */

function renderButton() {
  return (
    <Button>
      <StyledButtonContentDiv>
        <StyledIcon name="plusCircle" />
        <GridAreaItem area="text">
          <Trans id="circle-editor-teammate-dropdown-ok">Add members</Trans>
        </GridAreaItem>
      </StyledButtonContentDiv>
    </Button>
  );
}

function renderDisabledTooltip() {
  return <Trans id="circle-editor-teammate-dropdown-diabsled-tooltip">Already a member of this group.</Trans>;
}
